import { useState, useEffect, SyntheticEvent } from 'react';
import Box from '@mui/material/Box';
import axios from 'axios';
import { DateField, type DataTableColumn } from '@routes-plan/ui';
import { Office } from '../../models/office';
import { Route } from '../../models/route';
import { LoanReport } from '../../models/loan-report';
import { formatCurrency } from '../../utils/format/currency';
import moment from 'moment-timezone';
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';
import { useT } from '../../i18n';
import { setPageTitle } from '../../utils/pageTitle';
import { OfficeSelect, ReportEmpty, ReportFilters, ReportPage, ReportResults, ReportTable, RouteMultiSelect } from './components/ReportLayout';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface RefinanceRow {
  id: number | string;
  client: string;
  routeId?: string;
  route?: string;
  date?: string;
  initialValue: number;
  balance: number;
  daysLate: number | string;
  refinanceDate: string;
  refinanceTime: string;
  totalValue: number;
  paymentCount: number | string;
}

const Refinancing: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const t = useT();
  setPageTitle('menu.reports.refinancing');
  const [queried, setQueried] = useState(false);
  const current_date = nowOperationalDateString();

  const [office_id, setOffice_id] = useState('');
  const [collectsOffice, setCollectsOffice] = useState<Route[]>([]);
  const [selectedCollects, setSelectedCollects] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [start_date, setStart_date] = useState(current_date);
  const [end_date, setEnd_date] = useState(current_date);
  const [datas, setDatas] = useState<any[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const totalRowId = 'total-row';

  useEffect(() => {
    axios.get('offices').then(({ data }) => setOffices(data.data));
  }, []);

  useEffect(() => {
    const fetchRoutes = async () => {
      if (!office_id) return;

      showLoading();
      try {
        const response = await axios.get(`offices/${office_id}/routes`);
        setCollectsOffice(response.data);
        setSelectedCollects([]);
        setSelectAll(false);

        await new Promise((resolve) => setTimeout(resolve, 1000));

        if (!response.data || response.data.length === 0) {
          showAlert(t('loanReports.noRecordsForFilter'), 'info');
        }
      } catch (error) {
        console.error('Error fetching data', 'error');
      } finally {
        hideLoading();
      }
    };

    fetchRoutes();
  }, [office_id]);


  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!selectedCollects || selectedCollects.length === 0) {
      showAlert(t('loanReports.selectAtLeastOneRoute'), 'warning');
      return;
    }
    const formData = { endDate: end_date, startDate: start_date, routeId: selectedCollects };
    showLoading();
    setQueried(true);
    try {
      const response = await axios.post('/reports/refinancing', formData);
      setDatas(response.data.data);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!response.data.data || response.data.data.length === 0) {
        showAlert(t('loanReports.noRecordsForFilter'), 'info');
        return;
      }

    } catch (error) {
      console.error('Error:', error);
      // //console.log('Error fetching data:', 'error');
    } finally {
      hideLoading();
    }
  };

  const buildRows = (groupData: LoanReport[]): RefinanceRow[] => {
    if (!groupData || groupData.length === 0) return [];
    const mappedRows: RefinanceRow[] = groupData.map((r) => {
      const dateFormatted = moment(r.refinanceDate).format('YYYY-MM-DD');
      const timeFormatted = moment(r.refinanceDate).format('HH:mm:ss');
      return {
        id: r.id,
        client: r.client.fullName,
        routeId: String(r.route.id),
        route: r.route.name,
        date: dateFormatted,
        refinanceDate: dateFormatted,
        refinanceTime: timeFormatted,
        initialValue: Number(r.initialValue ?? 0),
        balance: Number(r.balance ?? 0),
        daysLate: r.daysLate ?? 0,
        totalValue: Number(r.totalValue ?? 0),
        paymentCount: r.paymentCount ?? 0,
      };
    });
    const totalInitialValue = mappedRows.reduce((acc, row) => acc + Number(row.initialValue || 0), 0);
    const totalBalance = mappedRows.reduce((acc, row) => acc + Number(row.balance || 0), 0);
    const totalRefinanced = mappedRows.reduce((acc, row) => acc + Number(row.totalValue || 0), 0);
    return [
      ...mappedRows,
      {
        id: totalRowId,
        client: t('loanReports.total'),
        initialValue: totalInitialValue,
        balance: totalBalance,
        daysLate: '-',
        date: undefined,
        refinanceDate: '-',
        refinanceTime: '-',
        totalValue: totalRefinanced,
        paymentCount: '-',
      },
    ];
  };

  const isTotal = (row: RefinanceRow) => row.id === totalRowId;
  const bold = (row: RefinanceRow, node: React.ReactNode) => (isTotal(row) ? <Box component="strong">{node}</Box> : node);
  const money = (field: 'initialValue' | 'balance' | 'totalValue', header: string): DataTableColumn<RefinanceRow> => ({
    id: field,
    header,
    align: 'right',
    value: (row) => Number(row[field] ?? 0),
    csv: (row) => formatCurrency(Number(row[field] ?? 0)),
    render: (row) => bold(row, formatCurrency(Number(row[field] ?? 0))),
  });

  const columns: DataTableColumn<RefinanceRow>[] = [
    { id: 'client', header: t('loanReports.client'), value: (row) => row.client, render: (row) => bold(row, row.client) },
    money('initialValue', t('loanReports.loanValue')),
    money('balance', t('loanReports.balance')),
    { id: 'daysLate', header: t('loanReports.daysLate'), align: 'right', value: (row) => (isTotal(row) ? '-' : String(row.daysLate ?? '-')) },
    { id: 'refinanceDate', header: t('common.date'), value: (row) => row.refinanceDate },
    { id: 'refinanceTime', header: t('loanReports.time'), value: (row) => row.refinanceTime, hideBelow: 'sm' },
    money('totalValue', t('loanReports.refinancedValue')),
    { id: 'paymentCount', header: t('loanReports.installments'), align: 'right', value: (row) => String(row.paymentCount ?? '-') },
  ];

  return (
    <ReportPage title={t('loanReports.refinancing.title')} description={t('loanReports.refinancing.description')}>
      <ReportFilters onSubmit={() => submit({ preventDefault: () => undefined } as SyntheticEvent)}>
        <DateField
          label={t('loanReports.startDate')}
          value={start_date}
          onChange={(v) => setStart_date(v ?? '')}
          max={minYmd(end_date, nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t('loanReports.endDate')}
          value={end_date}
          onChange={(v) => setEnd_date(v ?? '')}
          min={start_date}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <OfficeSelect offices={offices} value={office_id} onChange={setOffice_id} />
        <RouteMultiSelect
          routes={collectsOffice}
          value={selectedCollects}
          onChange={(ids) => {
            setSelectedCollects(ids);
            setSelectAll(ids.length > 0 && ids.length === collectsOffice.length);
          }}
        />
      </ReportFilters>

      {datas.length > 0 ? (
        datas.map((grupo: any) => (
          <ReportResults
            key={grupo.routeId}
            fill={datas.length === 1}
            title={t('loanReports.refinancing.groupTitle', { route: grupo.routeName })}
            meta={t('loanReports.dateRange', { start: start_date, end: end_date })}
          >
            <ReportTable<RefinanceRow>
              fill={datas.length === 1}
              rows={buildRows(grupo.data)}
              columns={columns}
              getRowId={(row) => row.id}
              searchable={false}
              exportFileName={t('loanReports.refinancing.exportFile')}
              ariaLabel={t('loanReports.refinancing.groupTitle', { route: grupo.routeName })}
            />
          </ReportResults>
        ))
      ) : (
        <ReportResults>
          <ReportEmpty queried={queried} />
        </ReportResults>
      )}
    </ReportPage>
  );
};

export default Refinancing;
